import React, { useEffect, useState, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const fmt = d => d ? new Date(d).toLocaleDateString('fr-FR') : '?';

const STATUS_LABELS = {
  DRAFT: 'Brouillon', SUBMITTED: 'Soumise', VALIDATED: 'Validée',
  SIGNING: 'En signature', SIGNED: 'Signée', ARCHIVED: 'Archivée', REJECTED: 'Rejetée',
};
const STATUS_COLORS = {
  DRAFT: 'bg-gray-100 text-gray-600', SUBMITTED: 'bg-yellow-100 text-yellow-700',
  VALIDATED: 'bg-blue-100 text-blue-700', SIGNING: 'bg-purple-100 text-purple-700',
  SIGNED: 'bg-green-100 text-green-700', ARCHIVED: 'bg-gray-100 text-gray-500',
  REJECTED: 'bg-red-100 text-red-700',
};

const PRESENCE_CONFIG = {
  true:  { label: 'Présent',        cls: 'bg-green-100 text-green-700' },
  false: { label: 'Non présent',    cls: 'bg-red-100 text-red-700' },
  null:  { label: 'Non renseigné',  cls: 'bg-gray-100 text-gray-500' },
};

const CRITERES_LABELS = {
  assiduite: 'Assiduité et ponctualité', integration: "Intégration dans l'équipe",
  initiative: 'Initiative et autonomie', tenue: 'Tenue professionnelle',
  qualite: 'Qualité du travail fourni', consignes: 'Respect des consignes',
  communication: 'Communication',
};
const NIVEAU_LABELS = { 1: 'Très insuffisant', 2: 'Insuffisant', 3: 'Satisfaisant', 4: 'Très satisfaisant' };
const NIVEAU_COLORS = {
  1: 'bg-red-100 text-red-700', 2: 'bg-orange-100 text-orange-700',
  3: 'bg-blue-100 text-blue-700', 4: 'bg-green-100 text-green-700',
};

const RELANCE_TYPES = [
  { type: 'presence',    label: 'Confirmer la présence' },
  { type: 'disponibilites', label: 'Indiquer les disponibilités' },
  { type: 'evaluation',  label: "Compléter l'évaluation (grille + appréciation générale)", types: ['grille', 'commentaire'] },
];

const VISITE_CONFIG = {
  A_PLANIFIER: { label: 'À planifier', cls: 'bg-gray-100 text-gray-500' },
  PLANIFIEE: { label: 'Planifiée', cls: 'bg-blue-100 text-blue-700' },
  EFFECTUEE: { label: 'Effectuée', cls: 'bg-green-100 text-green-700' },
};

export default function SuiviStage({ readOnly = false }) {
  const { user } = useAuth();
  const [zone, setZone] = useState('classes'); // 'classes' | 'assigned'
  const [classes, setClasses] = useState([]);
  const [assignedConventions, setAssignedConventions] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedConvention, setSelectedConvention] = useState(null);
  const [selectedClasseId, setSelectedClasseId] = useState(null);
  const [assignedFilterClasseId, setAssignedFilterClasseId] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cl, as, tc] = await Promise.all([
        api.get('/suivi/classes'),
        api.get('/suivi/assigned'),
        api.get('/suivi/teachers'),
      ]);
      setClasses(cl.data);
      setAssignedConventions(as.data);
      setTeachers(tc.data);
      if (cl.data.length > 0 && !selectedClasseId) setSelectedClasseId(cl.data[0].id);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (selectedConvention) {
    return (
      <ConventionDetail
        convention={selectedConvention}
        teachers={teachers}
        onBack={() => { setSelectedConvention(null); load(); }}
        onUpdated={load}
        isReferent={zone === 'classes'}
        currentUserId={user?.id}
        readOnly={readOnly}
      />
    );
  }

  const selectedClasse = classes.find(c => c.id === selectedClasseId);

  return (
    <div>
      {/* Zone selector */}
      {!readOnly && user?.role === 'teacher' && (
        <div className="flex gap-6 mb-6 border-b border-gray-200">
          {[
            { id: 'classes', label: 'Mes classes' },
            { id: 'assigned', label: 'Mes suivis individuels' },
          ].map(z => (
            <button key={z.id} onClick={() => setZone(z.id)}
              className={`pb-3 text-sm font-medium border-b-2 transition-colors ${zone === z.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {z.label}
              {z.id === 'assigned' && assignedConventions.length > 0 && (
                <span className="ml-1.5 bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5 rounded-full">
                  {assignedConventions.length}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {loading ? <div className="text-gray-500 text-sm">Chargement...</div> : (
        <>
          {/* Zone 1 — Mes classes */}
          {zone === 'classes' && (
            <div>
              {classes.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
                  Aucune classe assignée.
                </div>
              ) : (
                <>
                  {/* Sélecteur de classe */}
                  {classes.length > 1 && (
                    <div className="flex gap-2 mb-4 flex-wrap">
                      {classes.map(c => (
                        <button key={c.id} onClick={() => setSelectedClasseId(c.id)}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${selectedClasseId === c.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}>
                          {c.name}
                          <span className="ml-1 text-xs opacity-70">{c.schoolYear?.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedClasse && (
                    <ClasseView
                      key={selectedClasse.id}
                      classe={selectedClasse}
                      onSelectConvention={setSelectedConvention}
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* Zone 2 — Mes suivis individuels */}
          {zone === 'assigned' && (
            <div>
              {assignedConventions.length === 0 ? (
                <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400 text-sm">
                  Aucun suivi individuel assigné.
                </div>
              ) : (() => {
                const classeOptions = [...new Map(
                  assignedConventions.filter(c => c.classe).map(c => [c.classe.id, c.classe])
                ).values()];
                const filtered = assignedFilterClasseId
                  ? assignedConventions.filter(c => c.classe?.id === assignedFilterClasseId)
                  : assignedConventions;
                return (
                  <div>
                    {classeOptions.length > 1 && (
                      <div className="flex gap-2 mb-4 flex-wrap">
                        <button
                          onClick={() => setAssignedFilterClasseId('')}
                          className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${!assignedFilterClasseId ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}
                        >
                          Toutes
                        </button>
                        {classeOptions.map(cl => (
                          <button key={cl.id} onClick={() => setAssignedFilterClasseId(cl.id)}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${assignedFilterClasseId === cl.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'}`}>
                            {cl.name}
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="space-y-2">
                      {filtered.map(c => (
                        <ConventionRow key={c.id} convention={c} onClick={() => setSelectedConvention(c)} />
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SortHeader({ label, col, sortCol, sortDir, onSort }) {
  const active = sortCol === col;
  return (
    <th
      className="text-left px-4 py-2.5 text-xs font-medium text-gray-500 cursor-pointer select-none hover:text-gray-700"
      onClick={() => onSort(col)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className={`text-gray-300 ${active ? 'text-gray-500' : ''}`}>
          {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </span>
    </th>
  );
}

function ClasseView({ classe, onSelectConvention }) {
  const periodes = classe.periodes || [];
  const [selectedPeriodeId, setSelectedPeriodeId] = React.useState(
    periodes.length > 0 ? periodes[periodes.length - 1].id : '__all__'
  );
  const [sortCol, setSortCol] = React.useState(null);
  const [sortDir, setSortDir] = React.useState('asc');

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  // Filtrer les conventions selon la période sélectionnée
  const filteredConventions = selectedPeriodeId === '__all__'
    ? classe.conventions
    : selectedPeriodeId === '__none__'
      ? classe.conventions.filter(c => !c.stagePeriodeId)
      : classe.conventions.filter(c => c.stagePeriodeId === selectedPeriodeId);

  // Build map studentId -> convention : en cas de doublons, prioriser par statut significatif
  const STATUS_PRIORITY = { SIGNED: 0, SIGNING: 1, VALIDATED: 2, SUBMITTED: 3, REJECTED: 4, DRAFT: 5, ARCHIVED: 6 };
  const convByStudent = {};
  for (const conv of filteredConventions) {
    if (!conv.student?.id) continue;
    const existing = convByStudent[conv.student.id];
    if (!existing || (STATUS_PRIORITY[conv.status] ?? 99) < (STATUS_PRIORITY[existing.status] ?? 99)) {
      convByStudent[conv.student.id] = conv;
    }
  }

  const getSortKey = (student) => {
    const conv = convByStudent[student.id];
    if (sortCol === 'etudiant') return student.lastName?.toLowerCase() ?? '';
    if (sortCol === 'ville') return conv?.company?.city?.toLowerCase() ?? null;
    if (sortCol === 'suivi') return conv?.suiviAssignments?.[0]?.teacher?.lastName?.toLowerCase() ?? null;
    return null;
  };

  const sortedStudents = sortCol ? [...classe.students].sort(({ student: a }, { student: b }) => {
    const ka = getSortKey(a);
    const kb = getSortKey(b);
    if (ka === null && kb === null) return 0;
    if (ka === null) return 1;
    if (kb === null) return -1;
    return sortDir === 'asc' ? ka.localeCompare(kb) : kb.localeCompare(ka);
  }) : classe.students;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-800">{classe.name}
          <span className="ml-2 text-xs font-normal text-gray-400">{classe.schoolYear?.label}</span>
        </h3>
        <span className="text-xs text-gray-400">{classe.students.length} étudiant{classe.students.length > 1 ? 's' : ''}</span>
      </div>

      {/* Sélecteur de période */}
      {periodes.length > 0 && (
        <div className="flex gap-2 mb-4 flex-wrap">
          {periodes.map(p => (
            <button
              key={p.id}
              onClick={() => setSelectedPeriodeId(p.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                selectedPeriodeId === p.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
            >
              {p.nom}
              <span className="ml-1.5 text-xs opacity-70">{fmt(p.startDate)} → {fmt(p.endDate)}</span>
            </button>
          ))}
          {classe.conventions.some(c => !c.stagePeriodeId) && (
            <button
              onClick={() => setSelectedPeriodeId('__none__')}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                selectedPeriodeId === '__none__'
                  ? 'bg-gray-600 text-white border-gray-600'
                  : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
              }`}
            >
              Sans période
            </button>
          )}
        </div>
      )}


      {classe.students.length === 0 ? (
        <p className="text-sm text-gray-400">Aucun étudiant dans cette classe.</p>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <SortHeader label="Étudiant" col="etudiant" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Convention</th>
                <SortHeader label="Ville" col="ville" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Présence</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Appréciation</th>
                <th className="text-left px-4 py-2.5 text-xs font-medium text-gray-500">Visite</th>
                <SortHeader label="Suivi" col="suivi" sortCol={sortCol} sortDir={sortDir} onSort={handleSort} />
                <th className="px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {sortedStudents.map(({ student }) => {
                const conv = convByStudent[student.id];
                const a = conv?.appreciation;
                const presenceKey = a ? String(a.stagiairePresent) : 'null';
                const presence = PRESENCE_CONFIG[presenceKey] || PRESENCE_CONFIG['null'];
                const appComplete = a?.comment && a?.criteresEvaluation && Object.keys(a.criteresEvaluation).length > 0;
                const appPartial = a && !appComplete;
                const suivi = conv?.suiviAssignments?.[0]?.teacher;

                return (
                  <tr key={student.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{student.firstName} {student.lastName}</p>
                      <p className="text-xs text-gray-400">{student.email}</p>
                    </td>
                    <td className="px-4 py-3">
                      {conv ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[conv.status]}`}>
                          {STATUS_LABELS[conv.status]}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">{conv?.company?.city || <span className="text-gray-300">—</span>}</span>
                    </td>
                    <td className="px-4 py-3">
                      {conv ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${presence.cls}`}>
                          {presence.label}
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {conv && ['SIGNED', 'ARCHIVED'].includes(conv.status) ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${appComplete ? 'bg-green-100 text-green-700' : appPartial ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-500'}`}>
                          {appComplete ? 'Complète' : appPartial ? 'Partielle' : 'Manquante'}
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {conv && ['SIGNED', 'ARCHIVED'].includes(conv.status) ? (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${VISITE_CONFIG[conv.visiteStatus || 'A_PLANIFIER'].cls}`}>
                          {VISITE_CONFIG[conv.visiteStatus || 'A_PLANIFIER'].label}
                        </span>
                      ) : <span className="text-xs text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      {suivi ? (
                        <span className="text-xs text-gray-600">{suivi.firstName} {suivi.lastName}</span>
                      ) : (
                        <span className="text-xs text-gray-300">Non assigné</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {conv && (
                        <button onClick={() => onSelectConvention(conv)}
                          className="text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition-colors">
                          Voir
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ConventionRow({ convention: c, onClick }) {
  const a = c.appreciation;
  const presenceKey = a ? String(a.stagiairePresent) : 'null';
  const presence = PRESENCE_CONFIG[presenceKey] || PRESENCE_CONFIG['null'];
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-3 mb-1">
          <span className="font-medium text-gray-800">{c.student?.firstName} {c.student?.lastName}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[c.status]}`}>
            {STATUS_LABELS[c.status]}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${presence.cls}`}>
            {presence.label}
          </span>
        </div>
        <p className="text-xs text-gray-500">{c.company?.name} · {c.classe?.name} · {fmt(c.startDate)} → {fmt(c.endDate)}</p>
      </div>
      <button onClick={onClick}
        className="shrink-0 text-xs text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition-colors">
        Voir
      </button>
    </div>
  );
}

const VISITE_STATUS_CONFIG = {
  A_PLANIFIER: { label: 'À planifier', cls: 'bg-gray-100 text-gray-600 border-gray-300' },
  PLANIFIEE:   { label: 'Planifiée',   cls: 'bg-blue-100 text-blue-700 border-blue-300' },
  EFFECTUEE:   { label: 'Effectuée',   cls: 'bg-green-100 text-green-700 border-green-300' },
};
const VISITE_STATUS_SELECTED = {
  A_PLANIFIER: 'bg-gray-500 text-white border-gray-500',
  PLANIFIEE:   'bg-blue-600 text-white border-blue-600',
  EFFECTUEE:   'bg-green-600 text-white border-green-600',
};
const NOTE_LABELS = { 1: 'Très insuffisant', 2: 'Insuffisant', 3: 'Satisfaisant', 4: 'Très satisfaisant' };
const NOTE_COLORS = {
  1: { base: 'bg-red-100 text-red-700 border-red-300', sel: 'bg-red-500 text-white border-red-500' },
  2: { base: 'bg-orange-100 text-orange-700 border-orange-300', sel: 'bg-orange-500 text-white border-orange-500' },
  3: { base: 'bg-blue-100 text-blue-700 border-blue-300', sel: 'bg-blue-500 text-white border-blue-500' },
  4: { base: 'bg-green-100 text-green-700 border-green-300', sel: 'bg-green-500 text-white border-green-500' },
};

function VisiteBlock({ conv, currentUserId, isReferent, onUpdated, readOnly = false }) {
  const questions = conv.classe?.visiteQuestions || [];
  const myVisite = conv.visites?.find(v => v.teacherId === currentUserId);
  const hasPdfTemplate = !!conv.classe?.visitePdfTemplate;
  const [answers, setAnswers] = useState(myVisite?.answers || {});
  const [savingAnswers, setSavingAnswers] = useState(false);
  const [answersSaved, setAnswersSaved] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);

  const updateStatus = async (status) => {
    setSavingStatus(true);
    try {
      await api.put(`/suivi/${conv.id}/visite-status`, { status });
      if (onUpdated) onUpdated();
    } catch { alert('Erreur lors de la mise à jour du statut'); }
    finally { setSavingStatus(false); }
  };

  const saveAnswers = async () => {
    setSavingAnswers(true);
    try {
      await api.put(`/suivi/${conv.id}/visite-answers`, { answers });
      setAnswersSaved(true);
      setTimeout(() => setAnswersSaved(false), 2500);
      if (onUpdated) onUpdated();
    } catch { alert('Erreur lors de la sauvegarde'); }
    finally { setSavingAnswers(false); }
  };

  const uploadVisitePdf = async (file) => {
    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/suivi/${conv.id}/visite-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (onUpdated) onUpdated();
    } catch (e) {
      const msg = e.response?.status === 413
        ? 'Fichier trop volumineux (limite : 20 Mo)'
        : e.response?.data?.error || 'Erreur lors du dépôt du PDF';
      alert(msg);
    } finally {
      setUploadingPdf(false);
    }
  };

  const deleteVisitePdf = async () => {
    if (!window.confirm('Supprimer le PDF déposé ?')) return;
    try {
      await api.delete(`/suivi/${conv.id}/visite-pdf`);
      if (onUpdated) onUpdated();
    } catch {
      alert('Erreur lors de la suppression');
    }
  };

  const currentStatus = conv.visiteStatus || 'A_PLANIFIER';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="font-semibold text-gray-800 mb-4 text-sm">Visite de stage</h3>

      {/* Status */}
      <div className="mb-5">
        <p className="text-xs text-gray-500 mb-2">État de la visite</p>
        {readOnly ? (
          <span className={`text-xs px-2 py-1 rounded-full font-medium ${VISITE_STATUS_CONFIG[currentStatus].cls}`}>
            {VISITE_STATUS_CONFIG[currentStatus].label}
          </span>
        ) : (
          <div className="flex gap-2 flex-wrap">
            {Object.entries(VISITE_STATUS_CONFIG).map(([val, { label, cls }]) => (
              <button
                key={val}
                onClick={() => updateStatus(val)}
                disabled={savingStatus}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors disabled:opacity-50 ${
                  currentStatus === val ? VISITE_STATUS_SELECTED[val] : cls + ' hover:opacity-80'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* PDF Template Section */}
      {hasPdfTemplate && (
        <div className="mb-5 p-4 bg-blue-50 rounded-lg border border-blue-100">
          <p className="text-xs font-medium text-blue-800 mb-3">Formulaire de visite</p>
          
          {/* Bouton télécharger le PDF vierge */}
          <div className="flex flex-wrap gap-2 mb-3">
            <a
              href={`${process.env.REACT_APP_API_URL || ''}/uploads/${conv.classe.visitePdfTemplate}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-1.5 bg-white border border-blue-300 text-blue-700 text-xs font-medium rounded-lg hover:bg-blue-50 transition-colors"
            >
              📄 Télécharger le formulaire vierge
            </a>
          </div>

          {/* Zone de dépôt du PDF rempli */}
          {!readOnly && (
            <div>
              {myVisite?.pdfPath ? (
                <div className="flex items-center gap-3">
                  <span className="text-xs text-green-700 font-medium">✓ Formulaire rempli déposé</span>
                  <a
                    href={`/api/suivi/${conv.id}/visite-pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Voir
                  </a>
                  <button
                    onClick={deleteVisitePdf}
                    className="text-xs text-red-400 hover:text-red-600"
                  >
                    Supprimer
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-xs text-blue-600 mb-2">Déposez le formulaire rempli (PDF) :</p>
                  <input
                    type="file"
                    accept=".pdf"
                    id={`visite-pdf-upload-${conv.id}`}
                    className="hidden"
                    onChange={e => {
                      const file = e.target.files?.[0];
                      if (file) uploadVisitePdf(file);
                      e.target.value = '';
                    }}
                  />
                  <label
                    htmlFor={`visite-pdf-upload-${conv.id}`}
                    className="inline-block px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded-lg cursor-pointer transition-colors"
                  >
                    {uploadingPdf ? 'Dépôt...' : 'Déposer le formulaire rempli'}
                  </label>
                </div>
              )}
            </div>
          )}

          {/* Affichage du PDF rempli en lecture seule */}
          {(readOnly || isReferent) && myVisite?.pdfPath && (
            <div className="mt-2">
              <a
                href={`/api/suivi/${conv.id}/visite-pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800"
              >
                📄 Voir le formulaire rempli
              </a>
            </div>
          )}
        </div>
      )}

      {!hasPdfTemplate && !readOnly && (
        <p className="text-xs text-gray-400 mb-4">
          Aucun formulaire de visite PDF n'a été configuré pour cette classe.
          {isReferent && ' Déposez-le dans l\'onglet Classes > Configuration.'}
        </p>
      )}

      {/* Questionnaire form for current teacher — hidden in readOnly */}
      {!readOnly && !hasPdfTemplate && (questions.length > 0 ? (
        <div>
          <p className="text-xs font-medium text-gray-500 mb-3">Mon questionnaire de visite</p>
          <div className="space-y-4">
            {questions.map(q => (
              <div key={q.id}>
                <p className="text-sm text-gray-700 mb-1.5">{q.label}</p>
                {q.type === 'text' && (
                  <textarea
                    value={answers[q.id] || ''}
                    onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  />
                )}
                {q.type === 'oui_non' && (
                  <div className="flex gap-2">
                    {[
                      { v: 'Oui', cls: 'border-green-400 bg-green-50 text-green-700', sel: 'bg-green-500 text-white border-green-500' },
                      { v: 'Non', cls: 'border-red-300 bg-red-50 text-red-700', sel: 'bg-red-500 text-white border-red-500' },
                    ].map(({ v, cls, sel }) => (
                      <button key={v} type="button"
                        onClick={() => setAnswers(p => ({ ...p, [q.id]: p[q.id] === v ? null : v }))}
                        className={`px-4 py-1.5 rounded-lg border text-xs font-medium transition-colors ${answers[q.id] === v ? sel : cls}`}
                      >{v}</button>
                    ))}
                  </div>
                )}
                {q.type === 'note' && (
                  <div className="flex gap-2 flex-wrap">
                    {[1, 2, 3, 4].map(v => (
                      <button key={v} type="button"
                        onClick={() => setAnswers(p => ({ ...p, [q.id]: p[q.id] === v ? null : v }))}
                        className={`px-3 py-1.5 rounded-lg border text-xs font-medium transition-colors ${
                          answers[q.id] === v ? NOTE_COLORS[v].sel : NOTE_COLORS[v].base + ' hover:opacity-80'
                        }`}
                      >{NOTE_LABELS[v]}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3">
            <button onClick={saveAnswers} disabled={savingAnswers}
              className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-medium rounded-lg transition-colors">
              {answersSaved ? '✓ Enregistré' : savingAnswers ? '...' : 'Enregistrer mes réponses'}
            </button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-gray-400">
          Aucun questionnaire configuré pour cette classe.
          {isReferent && ' Configurez-le dans l\'onglet Classes > Configuration.'}
        </p>
      ))}

      {/* Réponses collectées — référent ou personnel (lecture seule) */}
      {(isReferent || readOnly) && conv.visites?.length > 0 && (
        <div className={!readOnly && questions.length > 0 && !hasPdfTemplate ? 'mt-5 pt-4 border-t border-gray-100' : ''}>
          <p className="text-xs font-medium text-gray-500 mb-3">
            Réponses collectées ({conv.visites.length} enseignant{conv.visites.length > 1 ? 's' : ''})
          </p>
          <div className="space-y-4">
            {conv.visites.map(v => (
              <div key={v.teacherId} className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs font-medium text-gray-600 mb-2">{v.teacher.firstName} {v.teacher.lastName}</p>
                {v.pdfPath ? (
                  <a
                    href={`/api/suivi/${conv.id}/visite-pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    📄 Formulaire PDF déposé
                  </a>
                ) : (
                  <div className="space-y-1.5">
                    {questions.map(q => v.answers?.[q.id] != null ? (
                      <div key={q.id} className="flex justify-between items-start gap-4 text-sm">
                        <span className="text-gray-600 text-xs">{q.label}</span>
                        <span className="text-gray-800 text-xs font-medium text-right shrink-0">
                          {typeof v.answers[q.id] === 'number' ? NOTE_LABELS[v.answers[q.id]] : v.answers[q.id]}
                        </span>
                      </div>
                    ) : null)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {readOnly && questions.length === 0 && !hasPdfTemplate && (
        <p className="text-xs text-gray-400">Aucun questionnaire de visite configuré pour cette classe.</p>
      )}
    </div>
  );
}

function ConventionDetail({ convention: initial, teachers, onBack, onUpdated, isReferent, currentUserId, readOnly = false }) {
  const [conv, setConv] = useState(initial);
  const [notes, setNotes] = useState(initial.teacherNotes || '');
  const [notesSaved, setNotesSaved] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);
  const [assignTeacherId, setAssignTeacherId] = useState('');
  const [relanceSent, setRelanceSent] = useState({});
  const [relanceLoading, setRelanceLoading] = useState({});

  const a = conv.appreciation;
  const presenceKey = a ? String(a.stagiairePresent) : 'null';
  const presence = PRESENCE_CONFIG[presenceKey] || PRESENCE_CONFIG['null'];

  const reloadConv = async () => {
    try {
      const res = await api.get('/suivi/classes');
      const allConventions = res.data.flatMap(c => c.conventions);
      const updated = allConventions.find(c => c.id === conv.id);
      if (updated) setConv(updated);
    } catch {}
    if (onUpdated) onUpdated();
  };

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/suivi/${conv.id}/notes`, { teacherNotes: notes });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2000);
    } catch { alert('Erreur lors de la sauvegarde'); }
    finally { setSavingNotes(false); }
  };

  const assign = async () => {
    if (!assignTeacherId) return;
    try {
      await api.post(`/suivi/${conv.id}/assign`, { teacherId: assignTeacherId });
      const res = await api.get('/suivi/classes');
      // Reload just this convention's assignments
      const allConventions = res.data.flatMap(c => c.conventions);
      const updated = allConventions.find(c => c.id === conv.id);
      if (updated) setConv(updated);
      setAssignTeacherId('');
      if (onUpdated) onUpdated();
    } catch (err) { alert(err.response?.data?.error || 'Erreur'); }
  };

  const unassign = async (teacherId) => {
    try {
      await api.delete(`/suivi/${conv.id}/assign/${teacherId}`);
      setConv(prev => ({
        ...prev,
        suiviAssignments: prev.suiviAssignments.filter(a => a.teacher.id !== teacherId),
      }));
      if (onUpdated) onUpdated();
    } catch { alert('Erreur lors de la suppression'); }
  };

  const sendRelance = async (type) => {
    setRelanceLoading(p => ({ ...p, [type]: true }));
    try {
      const relanceType = RELANCE_TYPES.find(r => r.type === type);
      const apiTypes = relanceType?.types || [type];
      await Promise.all(apiTypes.map(t => api.post(`/suivi/${conv.id}/relance`, { type: t })));
      setRelanceSent(p => ({ ...p, [type]: true }));
      setTimeout(() => setRelanceSent(p => ({ ...p, [type]: false })), 3000);
    } catch (err) { alert(err.response?.data?.error || "Erreur lors de l'envoi"); }
    finally { setRelanceLoading(p => ({ ...p, [type]: false })); }
  };

  // Determine which relances are relevant (element missing)
  const missingItems = {
    presence: !a || a.stagiairePresent === null || a.stagiairePresent === undefined,
    disponibilites: !a?.disponibilites,
    evaluation: (!a?.criteresEvaluation || Object.keys(a.criteresEvaluation).length === 0) || !a?.comment,
  };

  const canRelance = conv.tutorEmail && ['SIGNED', 'ARCHIVED'].includes(conv.status);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onBack} className="text-sm text-gray-500 hover:text-gray-700">← Retour</button>
        <h2 className="text-xl font-semibold text-gray-800">
          {conv.student?.firstName} {conv.student?.lastName}
        </h2>
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[conv.status]}`}>
          {STATUS_LABELS[conv.status]}
        </span>
      </div>

      <div className="space-y-4">
        {/* Infos stage */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Étudiant</p>
            <p className="text-gray-800">{conv.student?.firstName} {conv.student?.lastName}</p>
            <p className="text-xs text-gray-500">{conv.student?.email}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Classe</p>
            <p className="text-gray-800">{conv.classe?.name}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Entreprise</p>
            <p className="text-gray-800">{conv.company?.name}</p>
            <p className="text-xs text-gray-500">{conv.company?.address}, {conv.company?.city}</p>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Période</p>
            <p className="text-gray-800">{fmt(conv.startDate)} → {fmt(conv.endDate)}</p>
          </div>
        </div>

        {/* Fiche tuteur */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">Tuteur de stage</h3>
          {conv.tutorName ? (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Nom</p>
                <p className="text-gray-800">{conv.tutorFirstName} {conv.tutorName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Fonction</p>
                <p className="text-gray-800">{conv.tutorFunction || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Email</p>
                <p className="text-gray-800">{conv.tutorEmail || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Téléphone</p>
                <p className="text-gray-800">{conv.tutorPhone || '—'}</p>
              </div>
              {conv.tutorContactPreference && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-0.5">Préférence de contact</p>
                  <span className="text-xs bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full">
                    {conv.tutorContactPreference}
                  </span>
                </div>
              )}
              {a?.disponibilites && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Disponibilités</p>
                  <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-2 whitespace-pre-wrap">{a.disponibilites}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Tuteur non renseigné.</p>
          )}
        </div>

        {/* Retours tuteur */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">Retours du tuteur</h3>
          <div className="space-y-3">
            {/* Présence */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Présence au stage</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${presence.cls}`}>
                {presence.label}
              </span>
            </div>

            {/* Critères */}
            {a?.criteresEvaluation && Object.keys(a.criteresEvaluation).length > 0 ? (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-2">Évaluation par critères</p>
                <div className="space-y-1.5">
                  {Object.entries(a.criteresEvaluation).map(([key, val]) => val ? (
                    <div key={key} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{CRITERES_LABELS[key] || key}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${NIVEAU_COLORS[val]}`}>
                        {NIVEAU_LABELS[val]}
                      </span>
                    </div>
                  ) : null)}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400">Grille d'évaluation non remplie.</p>
            )}

            {/* Commentaire */}
            {a?.comment ? (
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Appréciation générale</p>
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{a.comment}</p>
              </div>
            ) : (
              <p className="text-xs text-gray-400">Appréciation générale non renseignée.</p>
            )}
          </div>
        </div>

        {/* Visite de stage */}
        <VisiteBlock
          conv={conv}
          currentUserId={currentUserId}
          isReferent={isReferent}
          onUpdated={reloadConv}
          readOnly={readOnly}
        />

        {/* Relances tuteur */}
        {!readOnly && canRelance && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">Relances tuteur</h3>
            <div className="space-y-2">
              {RELANCE_TYPES.map(({ type, label }) => (
                <div key={type} className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${missingItems[type] ? 'bg-orange-400' : 'bg-green-400'}`} />
                    <span className="text-sm text-gray-700">{label}</span>
                    {!missingItems[type] && <span className="text-xs text-gray-400">(déjà renseigné)</span>}
                  </div>
                  <button
                    onClick={() => sendRelance(type)}
                    disabled={!!relanceLoading[type]}
                    className="shrink-0 text-xs border px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 border-gray-200 text-gray-600 hover:border-gray-400 hover:text-gray-800"
                  >
                    {relanceSent[type] ? '✓ Envoyé' : relanceLoading[type] ? '...' : 'Relancer'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Attribution suivi */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-800 mb-3 text-sm">Enseignant(s) de suivi</h3>
          {conv.suiviAssignments?.length > 0 ? (
            <div className="space-y-2 mb-3">
              {conv.suiviAssignments.map(sa => (
                <div key={sa.teacher.id} className="flex items-center justify-between bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                  <span className="text-sm text-blue-800">{sa.teacher.firstName} {sa.teacher.lastName}</span>
                  {!readOnly && isReferent && (
                    <button onClick={() => unassign(sa.teacher.id)}
                      className="text-xs text-red-500 hover:text-red-700">Retirer</button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 mb-3">Aucun enseignant de suivi assigné.</p>
          )}
          {!readOnly && isReferent && (
            <div className="flex gap-2">
              <select value={assignTeacherId} onChange={e => setAssignTeacherId(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Choisir un enseignant...</option>
                {teachers.map(t => (
                  <option key={t.id} value={t.id}>{t.firstName} {t.lastName}</option>
                ))}
              </select>
              <button onClick={assign} disabled={!assignTeacherId}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm rounded-lg transition-colors">
                Assigner
              </button>
            </div>
          )}
        </div>

        {/* Remarques enseignant */}
        {readOnly ? (
          conv.teacherNotes && (
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm">Remarques</h3>
              <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 whitespace-pre-wrap">{conv.teacherNotes}</p>
            </div>
          )
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="font-semibold text-gray-800 mb-3 text-sm">Remarques (usage interne)</h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="Notes sur le déroulement du stage, observations lors de la visite..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">Visible uniquement par les enseignants.</span>
              <button onClick={saveNotes} disabled={savingNotes}
                className="px-4 py-1.5 bg-gray-800 hover:bg-gray-900 disabled:opacity-50 text-white text-xs rounded-lg transition-colors">
                {notesSaved ? '✓ Sauvegardé' : savingNotes ? '...' : 'Sauvegarder'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
